/**
 * Tasks talk back (#1645) — "Hand it back". When Done is pressed on a task
 * somebody else gave you, the server answers NOTE_REQUIRED; every quick-Done
 * surface (Tasks page, toolbar tray, field card) opens this instead of failing.
 * The one line goes straight to the person who asked, at the top of their list.
 *
 * Two-way part 2 (#1842): when the asker wrote a "Done looks like" line, it
 * shows here as a big tick box. Ticking is optional — an unticked box is an
 * honest "partly", and the asker sees which on Back to you.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle2, Circle } from "lucide-react";
import { firstName, isNoteRequired, noteRequiredDetails, UNDO_TOAST_MS, type Task } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const QUICK_LINES = ["Sent to the customer", "Called, left a voicemail", "Filed with the city", "Ordered"];

export function HandBackDialog({
    open,
    askerName,
    taskTitle,
    doneLooksLike,
    pending,
    onSubmit,
    onClose,
}: {
    open: boolean;
    askerName: string | null;
    taskTitle: string | null;
    /** The asker's "Done looks like" line, if they wrote one (#1842). */
    doneLooksLike?: string | null;
    pending?: boolean;
    /** `met` is undefined when there is no line to tick. */
    onSubmit: (note: string, met?: boolean) => void;
    onClose: () => void;
}) {
    const [note, setNote] = useState("");
    const [met, setMet] = useState(false);
    useEffect(() => {
        if (open) {
            setNote("");
            setMet(false);
        }
    }, [open]);
    const who = askerName ? askerName.split(" ")[0] : "The person who asked";
    return (
        <Dialog open={open} onClose={onClose} title="Hand it back">
            <div className="space-y-3">
                {taskTitle && <p className="text-sm font-medium">{taskTitle}</p>}
                {doneLooksLike && (
                    <button
                        type="button"
                        role="checkbox"
                        aria-checked={met}
                        data-testid="done-looks-like-tick"
                        onClick={() => setMet((m) => !m)}
                        className={
                            met
                                ? "flex w-full items-start gap-3 rounded-lg border-2 border-emerald-600 bg-emerald-50 px-3 py-2.5 text-left text-sm text-emerald-950 dark:bg-emerald-950 dark:text-emerald-100"
                                : "flex w-full items-start gap-3 rounded-lg border-2 border-dashed border-border bg-secondary px-3 py-2.5 text-left text-sm"
                        }
                    >
                        {met ? (
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                        ) : (
                            <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                        )}
                        <span>
                            <span className="block text-xs font-semibold uppercase tracking-wide opacity-70">
                                {who} said done looks like
                            </span>
                            {doneLooksLike}
                        </span>
                    </button>
                )}
                <div className="space-y-1.5">
                    <Label htmlFor="hand-back-note">
                        What happened? <span className="text-destructive">Required</span>
                    </Label>
                    <Textarea
                        id="hand-back-note"
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="One line is plenty."
                        autoFocus
                    />
                    <div className="flex flex-wrap gap-1.5">
                        {QUICK_LINES.map((q) => (
                            <Button
                                key={q}
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-full text-xs"
                                onClick={() => setNote((n) => (n.trim() ? `${n.trim()} ${q}.` : `${q}.`))}
                            >
                                {q}
                            </Button>
                        ))}
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    {who} gets this at the top of their Tasks page and confirms it was handled.
                </p>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        Not yet
                    </Button>
                    <Button
                        className="bg-emerald-700 text-white hover:bg-emerald-800"
                        disabled={!note.trim() || pending}
                        onClick={() => onSubmit(note.trim(), doneLooksLike ? met : undefined)}
                    >
                        Hand back to {who}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

interface FinishTarget {
    id: string;
    title?: string | null;
    askerName?: string | null;
    doneLooksLike?: string | null;
}

/**
 * The one Done button every surface shares. Presses Done; if the server says
 * this task goes back to someone (NOTE_REQUIRED), asks for the line and tries
 * again. Undo is the same reopen it always was.
 */
export function useFinishTask(onChanged: () => void) {
    const [asking, setAsking] = useState<FinishTarget | null>(null);
    const mut = useMutation({
        mutationFn: ({ target, note, met }: { target: FinishTarget; note?: string; met?: boolean }) =>
            api.post<{ task: Task; partDone?: boolean; left?: number }>(
                `/api/tasks/${target.id}/done`,
                note ? { resolutionNote: note, ...(met !== undefined ? { doneLooksLikeMet: met } : {}) } : {},
            ),
        onSuccess: (res, { target }) => {
            setAsking(null);
            onChanged();
            const handedBack = res?.task?.status === "handed_back";
            const who = firstName(res?.task?.reporterName ?? target.askerName);
            // Shared, everyone must finish (10/16): only my part is in.
            const left = res?.partDone ? (res.left ?? 0) : 0;
            const line = res?.partDone
                ? `Your part is done. ${left} ${left === 1 ? "person" : "people"} still to go.`
                : handedBack
                  ? `Done. ${who} sees a green check.`
                  : "Done";
            toast.success(line, {
                duration: UNDO_TOAST_MS,
                action: {
                    label: "Undo",
                    onClick: () => {
                        api.post(`/api/tasks/${target.id}/reopen`)
                            .then(() => {
                                onChanged();
                                toast.success("Task reopened.");
                            })
                            .catch(() => toast.error("Undo failed — reopen it from the task."));
                    },
                },
            });
        },
        onError: (e, { target }) => {
            if (isNoteRequired(e)) {
                // The server says who asked and what "done" looks like, so
                // every surface shows the same dialog without loading the task.
                const more = noteRequiredDetails(e);
                setAsking({
                    ...target,
                    askerName: target.askerName ?? more.askerName,
                    doneLooksLike: target.doneLooksLike ?? more.doneLooksLike,
                });
                return;
            }
            toast.error(e instanceof Error ? e.message : "Failed");
        },
    });
    const dialog = (
        <HandBackDialog
            open={!!asking}
            askerName={asking?.askerName ?? null}
            taskTitle={asking?.title ?? null}
            doneLooksLike={asking?.doneLooksLike ?? null}
            pending={mut.isPending}
            onSubmit={(note, met) => asking && mut.mutate({ target: asking, note, met })}
            onClose={() => setAsking(null)}
        />
    );
    return { finish: (target: FinishTarget) => mut.mutate({ target }), pending: mut.isPending, dialog };
}
